import React from "react"
import { twMerge } from "tailwind-merge"

import evergreen1 from "../../../assets/quote_patterns/evergreen_1.png"
import evergreen2 from "../../../assets/quote_patterns/evergreen_2.png"
import geometricSvg from "../../../assets/quote_patterns/geometric.svg"
import leavesPng from "../../../assets/quote_patterns/leaves.png"
import oceanPng from "../../../assets/quote_patterns/quote-ocean.png"
import waveSvg from "../../../assets/quote_patterns/wave.svg"
import AnimatedSection from "../../ProProfileShared/AnimatedSection"
import { DEFAULT_HIGHLIGHT_CLASSES } from "../WebsiteBuilderV2Page"

const QuoteSectionDesktop = ({ section, theme, highlightedField }) => {
  const isHighlighted = highlightedField === `section-${section.id}`
  const settings = section.settings || {}

  // Quote settings
  const quoteAuthor = settings.quoteAuthor || ""
  const rawPattern = (settings.backgroundPattern || "leaves").toLowerCase()
  const legacyToNew = { water: "ocean", pine: "evergreen", swoop: "wave" }
  const backgroundPattern = (legacyToNew[rawPattern] || rawPattern).toLowerCase()

  const palette = {
    textLight: theme?.colors?.textLight || "#FFFFFF",
    textDark: theme?.colors?.textDark || "#0b3954",
    primaryDark: theme?.colors?.primaryDark || "#8B6F6B",
    secondary: theme?.colors?.secondary || "#e9dfdc",
    bgDark: theme?.colors?.bgDark || "#efeae8"
  }

  // Get background pattern styles
  const getBackgroundPattern = () => {
    switch (backgroundPattern) {
      case "leaves":
        return { backgroundColor: palette.primaryDark }
      case "ocean":
        return { backgroundColor: palette.primaryDark }
      case "evergreen":
        return { backgroundColor: palette.primaryDark }
      case "wave":
        return { backgroundColor: palette.primaryDark }
      case "geometric":
        return { backgroundColor: palette.primaryDark }
      default:
        return { backgroundColor: palette.primaryDark }
    }
  }

  return (
    <section
      className={twMerge("relative overflow-hidden py-16 lg:py-24", isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : "")}
      style={{
        ...getBackgroundPattern(),
        width: "100vw"
      }}>
      {/* Ocean background image (full-bleed) */}
      {backgroundPattern === "ocean" && (
        <img
          src={oceanPng}
          alt=""
          aria-hidden
          className="pointer-events-none absolute inset-0 h-full w-full select-none object-cover"
        />
      )}
      {/* Tone overlays for Ocean and Geometric to match Figma darkness */}
      {backgroundPattern === "ocean" && (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            backgroundColor: palette.primaryDark,
            opacity: 0.33,
            mixBlendMode: "multiply"
          }}
        />
      )}
      {backgroundPattern === "geometric" && (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            backgroundColor: palette.primaryDark,
            opacity: 0.18,
            mixBlendMode: "multiply"
          }}
        />
      )}

      {/* Decorative overlays for Leaves and Evergreen */}
      {backgroundPattern === "leaves" && (
        <>
          <img
            src={leavesPng}
            alt=""
            className="pointer-events-none absolute select-none"
            style={{
              width: "30%",
              minWidth: "600px",
              height: "auto",
              left: "-12%",
              transform: "rotate(-32deg)",
              opacity: 0.33,
              mixBlendMode: "multiply"
            }}
          />
          <img
            src={leavesPng}
            alt=""
            className="pointer-events-none absolute select-none sm:hidden"
            style={{
              top: "40%",
              right: "-3%",
              width: "40%",
              minWidth: "350px",
              height: "auto",
              transform: "rotate(-200deg)",
              opacity: 0.24,
              mixBlendMode: "multiply"
            }}
          />
        </>
      )}

      {backgroundPattern === "evergreen" && (
        <>
          <img
            src={evergreen2}
            alt=""
            className="pointer-events-none absolute select-none"
            style={{
              bottom: "-120px",
              left: "-160px",
              width: "780px",
              height: "auto",
              transform: "scaleX(-1) rotate(-10deg)",
              opacity: 0.28,
              mixBlendMode: "multiply"
            }}
          />
          <img
            src={evergreen1}
            alt=""
            className="pointer-events-none absolute select-none"
            style={{
              top: "-100px",
              right: "-150px",
              width: "660px",
              height: "auto",
              transform: "scaleX(-1) rotate(-12deg)",
              opacity: 0.22,
              mixBlendMode: "multiply"
            }}
          />
        </>
      )}

      {/* Wave overlay: match Figma (container w=1440, top=45px, flipped) */}
      {backgroundPattern === "wave" && (
        <>
          <img
            src={waveSvg}
            alt=""
            className="pointer-events-none absolute select-none"
            style={{
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              transform: "scaleX(-1) scaleY(-1)",
              opacity: 0.44,
              mixBlendMode: "multiply"
            }}
          />
        </>
      )}

      {/* Geometric overlay: full-bleed like Wave (cover, flipped) */}
      {backgroundPattern === "geometric" && (
        <>
          <img
            src={geometricSvg}
            alt=""
            className="pointer-events-none absolute select-none"
            style={{
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              transform: "scaleY(-1)",
              opacity: 0.99,
              mixBlendMode: "multiply"
            }}
          />
        </>
      )}

      {/* Tone overlays for Ocean and Geometric to match Figma darkness */}
      {backgroundPattern === "ocean" && (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{ backgroundColor: palette.textDark, opacity: 0.18, mixBlendMode: "multiply" }}
        />
      )}
      {backgroundPattern === "geometric" && (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{ backgroundColor: palette.textDark, opacity: 0.1, mixBlendMode: "multiply" }}
        />
      )}

      <AnimatedSection className="relative z-10 mx-auto max-w-6xl px-5 md:px-4">
        <div className="flex min-h-[300px] items-center justify-center text-center">
          <div className="max-w-4xl">
            {/* Quote Text */}
            <blockquote
              className="mb-6 text-[27px] font-medium leading-[32px] xl:text-4xl lg:text-5xl md:text-3xl md:leading-relaxed"
              style={{
                fontFamily: theme?.fonts?.heading || "serif",
                fontWeight: theme?.fonts?.headingWeight || "700",
                color: palette.textLight
              }}>
              &quot;{section.body || "Your quote text will appear here..."}&quot;
            </blockquote>

            {/* Quote Author */}
            {quoteAuthor && (
              <div
                className="text-[18px] font-semibold leading-[24px] lg:text-2xl md:text-xl"
                style={{
                  fontFamily: theme?.fonts?.heading || "serif",
                  fontWeight: theme?.fonts?.headingWeight || "700",
                  color: palette.textLight
                }}>
                — {quoteAuthor}
              </div>
            )}
          </div>
        </div>
      </AnimatedSection>
    </section>
  )
}

export default QuoteSectionDesktop
